import React from 'react';
import {
  ExternalLink, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';

// Builds the HubSpot record URL for a contact or deal
const buildHubspotUrl = (portalId, type, recordId) => {
  if (!portalId || !recordId) return null;
  // type: "contact" or "deal"
  return `https://app.hubspot.com/contacts/${portalId}/record/${type === 'deal' ? '0-3' : '0-1'}/${recordId}`;
};

// Compact clickable HubSpot chip — used on inquiry rows, CRM cards
export const HubspotRecordLink = ({ portalId, contactId, dealId, compact = false }) => {
  if (!portalId || (!contactId && !dealId)) return null;
  const linkClass = 'inline-flex items-center gap-1 text-[#FF7A59] hover:text-[#FF9B82] text-xs transition-colors';
  return (
    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
      {dealId && (
        <a
          href={buildHubspotUrl(portalId, 'deal', dealId)}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          data-testid="hubspot-deal-link"
          title="Open deal in HubSpot"
        >
          <ExternalLink size={12} />
          {!compact && 'HubSpot Deal'}
        </a>
      )}
      {contactId && !dealId && (
        <a
          href={buildHubspotUrl(portalId, 'contact', contactId)}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          data-testid="hubspot-contact-link"
          title="Open contact in HubSpot"
        >
          <ExternalLink size={12} />
          {!compact && 'HubSpot Contact'}
        </a>
      )}
    </div>
  );
};

// Full banner shown at top of admin overview — connection + pipeline status
export const HubspotStatusBanner = ({ status, onRefresh }) => {
  if (!status) {
    return (
      <div className="bg-[#0a0a0a] border border-white/10 p-5 mb-8 flex items-center gap-4 animate-pulse" data-testid="hubspot-status-loading">
        <div className="w-5 h-5 rounded-full bg-white/10" />
        <p className="text-white/40 text-sm">Checking HubSpot status…</p>
      </div>
    );
  }

  const ok = status.connected && status.pipeline_configured && status.stages_mapped === status.stages_total;
  const warn = status.connected && (!status.pipeline_configured || status.stages_mapped < status.stages_total);
  const fail = !status.connected;

  const style = ok
    ? 'bg-[#0a1a0a] border-green-600/30'
    : warn
      ? 'bg-[#1a140a] border-[#D4AF37]/30'
      : 'bg-[#1a0a0a] border-red-600/30';

  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  const iconColor = ok ? 'text-green-400' : warn ? 'text-[#D4AF37]' : 'text-red-400';
  const label = ok ? 'HubSpot Connected' : warn ? 'HubSpot Connected — Pipeline Action Needed' : 'HubSpot Disconnected';

  return (
    <div className={`border p-5 mb-8 ${style}`} data-testid="hubspot-status-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <p className="text-white text-sm font-medium">{label}</p>
              {status.connected && (
                <span className="text-[10px] uppercase tracking-widest text-white/40">
                  Portal #{status.portal_id}
                </span>
              )}
            </div>
            <p className="text-white/60 text-sm leading-relaxed">{status.message}</p>
            {status.connected && (
              <div className="mt-3 flex items-center gap-4 text-xs text-white/50">
                <span>
                  Pipeline:{' '}
                  <span className={status.pipeline_configured ? 'text-green-400' : 'text-[#D4AF37]'}>
                    {status.pipeline_configured ? status.pipeline_name : 'Not configured'}
                  </span>
                </span>
                <span>
                  Stages mapped:{' '}
                  <span className={status.stages_mapped === status.stages_total ? 'text-green-400' : 'text-[#D4AF37]'}>
                    {status.stages_mapped}/{status.stages_total}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="text-white/60 hover:text-white p-1.5 border border-white/15 hover:border-white/30 transition-colors"
          title="Re-check HubSpot status"
          data-testid="hubspot-refresh-btn"
        >
          <RefreshCw size={14} />
        </button>
      </div>
      {fail && (
        <a
          href="https://app.hubspot.com/settings/integrations/private-apps"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-4 text-[#FF7A59] hover:text-[#FF9B82] text-xs"
        >
          <ExternalLink size={12} /> Open HubSpot private apps
        </a>
      )}
    </div>
  );
};
